<template>
    <v-dialog max-width="900"
              v-model="dialogModel"
              :data-variables="dataVariables">

        <toolbar-page :title="titleText" icon="mdi-link" help="edit-entry-link-template-dialog">
            <template slot="buttons">

                <v-tooltip top color="primary">
                    <template v-slot:activator="{ on }">
                        <v-btn icon v-on="on" @click="close">
                            <v-icon>mdi-close</v-icon>
                        </v-btn>
                    </template>
                    <span>Close</span>
                </v-tooltip>
            </template>
        </toolbar-page>

        <v-card>

            <v-card-text class="pt-2">

                <title-card title="Settings" no-padding-top>
                    <v-container class="pa-0">

                        <v-row dense>
                            <v-col cols="12" v-if="editTemplate">

                                <v-text-field v-model="data.name"
                                              label="Template name"
                                              clearable
                                              hide-details
                                              placeholder="Enter descriptive name"
                                              :error-messages="errors.name ? errors.base.name : []"
                                ></v-text-field>
                            </v-col>

                            <v-col cols="12">

                                <v-text-field v-model="data.base_url"
                                              label="Base URL"
                                              clearable
                                              hide-details
                                              placeholder="Enter base URL"
                                              :error-messages="errors.base_url ? errors.base_url : []"
                                ></v-text-field>
                            </v-col>

                            <v-col cols="6">
                                <v-select
                                    :items="algorithms"
                                    v-model="data.hash_algo"
                                    hide-details
                                    label="Hash algorithm"
                                ></v-select>
                            </v-col>

                            <v-col cols="6" v-show="data.hash_algo!=='None'">
                                <v-text-field v-model="data.hash_param"
                                              label="Signature param name (required)"
                                              hide-details
                                              :error-messages="errors.hash_algo ? errors.hash_algo : []"
                                ></v-text-field>
                            </v-col>

                            <v-col cols="6" v-show="data.hash_algo!=='None'">
                                <v-text-field v-model="data.hash_secret"
                                              label="Signature shared secret matching target platform (required)"
                                              hide-details
                                              :error-message="errors.hash_secret ? errors.hash_secret : []"
                                ></v-text-field>
                            </v-col>

                            <v-col cols="6">
                                <v-text-field v-model="data.pid_param"
                                              label="Panelist ID param name (required)"
                                              hide-details
                                              :error-messages="errors.pid_param ? errors.pid_param : []"
                                ></v-text-field>
                            </v-col>
                        </v-row>
                    </v-container>
                </title-card>

                <!-- DATA VARIABLE PIPES AND PARAMS -->
                <title-card title="Data pipes" no-padding-top class="mt-2">
                    <v-container class="pa-0">
                        <v-row no-gutters>

                            <v-col cols="4" v-for="param in params" :key="param.data_variable_id">
                                <div class="d-flex">

                                    <v-text-field v-model="param.name"
                                                  class="align-self-center"
                                                  :hint="param.variable_name"
                                                  persistent-hint
                                    ></v-text-field>

                                    <v-btn icon @click="variableDeleted(param)" class="align-self-center mr-2">
                                        <v-icon size="20">mdi-delete</v-icon>
                                    </v-btn>
                                </div>
                            </v-col>

                        </v-row>

                        <v-row no-gutters>
                            <v-col cols="6" class="pt-3 align-center">
                                <v-btn color="primary" small @click="dataVariablePickerModel=true">
                                    Select data variables
                                </v-btn>
                            </v-col>
                        </v-row>

                    </v-container>
                </title-card>

                <!-- EXAMPLE LINK -->
                <title-card title="Example URL" class="pt-2">
                    {{ exampleUrl }}
                </title-card>

                <!-- ACTION BUTTONS -->
                <v-card-actions>

                    <v-btn v-if="!editTemplate" color="primary" @click="use">{{ actionButtonText }}</v-btn>

                    <div v-if="editTemplate">

                        <!-- SAVE BUTTON -->
                        <v-btn color="primary"
                               v-if="creating"
                               class="ml-2"
                               :loading="saving"
                               @click="save">
                            Create
                        </v-btn>

                        <!-- UPDATE BUTTON -->
                        <v-btn color="primary"
                               v-if="!creating"
                               class="ml-2"
                               @click="update">
                            Save changes
                        </v-btn>
                    </div>

                    <!-- CANCEL BUTTON -->
                    <v-btn color="secondary" @click="close" class="ml-2">Cancel</v-btn>
                </v-card-actions>

            </v-card-text>

            <!-- DATA VARIABLE PICKER -->
            <data-variable-picker
                v-model="dataVariablePickerModel"
                :data-variables="dataVariables"
                :multiple="true"
                :selected="selectedVariables"
                @change="dataVariablesSelected"
            ></data-variable-picker>

            <json v-if="false" :data="data"></json>

        </v-card>

    </v-dialog>

</template>

<script>

import {mapActions, mapState} from 'vuex';
import ToolbarPage from "../../../components/UI/ToolbarPage";
import DataVariablePicker from "../../../components/UI/DataVariablePicker";
import TitleCard from "../../../components/UI/TitleCard";
import TitleCardList from "../../../components/UI/TitleCardList";
import Json from 'vue-json-pretty';
import ExampleLinkBuilder from "../ts/ExampleLinkBuilder";

const vuexModule = "LinksManager";

export default {

    name: "EditLink",

    components: {
        ToolbarPage,
        DataVariablePicker,
        TitleCard,
        TitleCardList,
        Json
    },

    props: {
        value: {
            required: true,
            type: Boolean,
        },
        link: {
            required: true,
            type: Object | null,
        },
        dataVariables: {
            required: true,
            type: Array
        },
        editTemplate: {
            required: true,
            type: Boolean
        }
    },

    data() {
        return {
            debug: false,
            data: {
                name: 'New template',
                hash_algo: 'None',
                hash_param: 'sign',
                hash_secret: '!Secret!',
                pid_param: 'panelist_id',
                base_url: 'https://surveyengine.com?project_id=100',
                example_url: null,
                params: [],
            },
            dataVariablePickerModel: false,
            selectedVariables: [],
            algorithms: ['None', 'MD5', 'SHA1', 'SHA256'],
            tabModel: null,
            errors: [],
        }
    },

    mounted() {
        if (this.link) {
            this.prepareData();
        }
    },

    watch: {
        link(value) {
            if (value) {
                this.prepareData();
            }
        }
    },

    computed: {
        ...mapState(vuexModule, [
            'entryLinks',
            'saving'
        ]),

        params: {
            get() {
                if (this.dataVariables && this.link) {
                    return this.data.params;
                }
            },
            set(params) {
                this.data.params = params;
            }
        },

        exampleUrl() {
            return new ExampleLinkBuilder(this.data).get();
        },

        creating() {
            return !this.link || !this.link.id;
        },

        titleText() {
            if (this.editTemplate) {

                if (this.creating) {
                    return 'Entry link template';
                } else {
                    return 'Entry link template';
                }
            } else {
                return 'Entry link';
            }
        },

        actionButtonText() {
            if (this.editTemplate) {
                return 'Apply';

            } else {
                return 'Apply';
            }
        },

        dialogModel: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit('input', value);
            }
        }
    },

    methods: {
        ...mapActions(vuexModule, [
            'saveEntryLink',
            'updateEntryLink'
        ]),

        variableDeleted(param) {

            const index = this.data.params.findIndex(p => p.data_variable_id === param.data_variable_id);

            this.data.params.splice(index, 1);

            let selected = [];

            this.data.params.forEach(param => {
                const dataVariable = this.dataVariables.find(variable => variable.id === param.data_variable_id);
                selected.push(dataVariable);
            });

            this.selectedVariables = selected;

        },

        prepareData() {

            if (this.link) {
                this.data = Object.assign({}, this.link);
            }

            if (this.link && this.link.params && this.dataVariables) {
                this.link.params.forEach(param => {
                    this.createSelectedVariables(param)
                })
            }

            this.data.example_url = new ExampleLinkBuilder(this.data).get();
        },

        save() {

            this.saveEntryLink(this.data)
                .then(() => {
                    Bus.$emit('flash-message', {type: 'info', message: 'Template saved!'});
                    this.close();
                })
                .catch((response) => {
                    Bus.$emit('flash-message', {type: 'error', message: 'OOPS! Failed saving!'});
                });
        },

        update() {

            this.updateEntryLink(this.data)
                .then(() => {
                    Bus.$emit('flash-message', {type: 'info', message: 'Template saved!'});
                    this.close();
                })
                .catch((error) => {

                    console.log(error);

                    if (error.response.status === 422) {

                        this.errors = error.response.data.errors;
                    }

                    Bus.$emit('flash-message', {type: 'error', message: 'OOPS! Failed saving!'});
                });
        },

        use() {
            let useData = Object.assign(this.data, {example_url: this.data.example_url});

            this.$emit('use', useData);
            this.$emit('input', false);
        },

        close() {
            this.$emit('input', false);
        },

        dataVariablesSelected(variables) {

            this.selectedVariables = [...variables];

            this.appendNew();
            this.removeDeleted();
        },

        appendNew() {
            this.selectedVariables.forEach(selectedVariable => {

                const found = this.data.params.find(linkVariable => linkVariable.data_variable_id === selectedVariable.id);

                if (!found) {
                    this.createLinkVariable(selectedVariable);
                }
            });
        },

        removeDeleted() {
            this.data.params = this.data.params.filter(linkVariable => {
                return !!this.selectedVariables.find(selectedVariable => linkVariable.data_variable_id === selectedVariable.id)
            });
        },

        createLinkVariable(variable) {
            let linkVariable = {
                data_variable_id: variable.id,
                name: variable.name.toLowerCase(),
                variable_name: variable.name
            };
            this.data.params.push(linkVariable);
        },

        createSelectedVariables(param) {
            const dataVariable = this.dataVariables.find(variable => variable.id === param.data_variable_id);

            if (dataVariable) {
                param.variable_name = dataVariable.name;
                this.selectedVariables.push(dataVariable);
            }
        }
    },
}
</script>
